import React, { useEffect, useState } from 'react';
import { FieldProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import {
  CardHcd,
  ProfessionalHcd,
} from '@maiia/model/generated/model/api-patient/api-patient';
import { Box, Button } from '@mui/material';
import {
  practitionersActions,
  temporaryAppointmentsActions,
} from '../../../src/actions';

import SelectDialog from './SelectDialog';
import { useTranslation } from '@/src/i18n';

import { NO_PRAT_PREFER, NO_PRAT_ONLINE } from '@/src/constants';
import { isNoPractitionerSelected } from '../../utils/card';
import Loader from '@/components/atoms/Loader/Loader';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import { isDemoAccountSelector } from '@/src/selectors';

interface Props extends FieldProps {
  card: CardHcd;
  onReset: () => any;
  value: string;
}

const noPractitionerInfo: CardHcd = {
  id: NO_PRAT_PREFER,
  professional: {
    practitionerId: NO_PRAT_PREFER,
    firstName: "Je n'ai pas de préférence !",
    lastName: '',
    speciality: { name: '', isParamedical: true },
  },
  settings: { isVisibleOnline: true },
  type: 'PRACTITIONER',
};

const noPractitionerOnlineInfo: CardHcd = {
  id: NO_PRAT_ONLINE,
  professional: {
    practitionerId: NO_PRAT_ONLINE,
    firstName: 'Attribué par le centre',
    lastName: '',
    speciality: { name: '', isParamedical: true },
  },
  type: 'PRACTITIONER',
};

const practitionerItemsSelector = specialityId => (state: {
  practitioners: { items: Array<any> };
}) =>
  state?.practitioners?.items?.filter(
    item =>
      item?.professional?.speciality?.id === specialityId &&
      item.settings.isOnlineAppointment,
  );

const PractitionerSelect: React.FC<Props> = (props: any) => {
  const {
    field,
    form: { setFieldValue, values },
    card,
    onReset,
    ...rest
  } = props;
  const { consultationReasonName, centerId, specialityId } = values;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const isDemoAccount = useSelector(isDemoAccountSelector);
  const [practitionerSelected, setPractitionerSelected] = React.useState<
    ProfessionalHcd | undefined
  >();
  const practitionersSelector = React.useMemo(
    () => practitionerItemsSelector(specialityId),
    [specialityId],
  );
  const practitioners = useSelector(
    practitionersSelector,
    (oldValue: Array<any>, newValue: Array<any>) =>
      isEqual(
        oldValue.map(({ id }) => id),
        newValue.map(({ id }) => id),
      ),
  );
  const isLoading = useSelector(
    (state: any) => state?.practitioners?.isLoading,
  );

  const practitionersOnline = React.useMemo(
    () => practitioners.filter(item => item?.settings?.isVisibleOnline),
    [practitioners],
  );

  const practitionersOnlineWithNoPrefer = React.useMemo(
    () => [{ ...noPractitionerInfo }, ...practitionersOnline],
    [practitionersOnline],
  );

  const nPractitionerOnline = React.useMemo(() => practitionersOnline.length, [
    practitionersOnline,
  ]);

  const updatePractitioner = React.useCallback((practitionerObj: CardHcd) => {
    setPractitionerSelected(practitionerObj?.professional);
    const practitionerId = practitionerObj?.professional?.practitionerId;
    if (isNoPractitionerSelected(practitionerId)) {
      dispatch(
        temporaryAppointmentsActions.mergeItem({
          cardHcd: undefined,
          practitioner: undefined,
        }),
      );
      return;
    }
    dispatch(practitionersActions.getActions().setItem(practitionerObj));
    dispatch(
      temporaryAppointmentsActions.mergeItem({
        cardHcd: { ...practitionerObj },
        practitioner: {
          ...practitionerObj,
        },
      }),
    );
  }, []);

  useEffect(() => {
    dispatch(practitionersActions.getActions().reset());
    dispatch(
      practitionersActions.getActions(isDemoAccount).getList({
        rootCenterId: centerId,
        consultationReasonName,
        specialityId,
      }),
    );
  }, [centerId, consultationReasonName, specialityId]);

  React.useEffect(() => {
    // If have practitioners
    if (practitioners.length > 0) {
      // If no practitioners online -> show assgined by center
      // else if one practitioner online -> choose practitioner online
      // else more than one practitioner online -> i don't have any preference

      let firstPractitioner: CardHcd = noPractitionerInfo;
      if (nPractitionerOnline === 0) {
        firstPractitioner = noPractitionerOnlineInfo;
      } else if (nPractitionerOnline === 1) {
        firstPractitioner = practitionersOnline?.[0];
      }

      setFieldValue(
        field.name,
        firstPractitioner?.professional?.practitionerId,
      );
      updatePractitioner(firstPractitioner);
    }
  }, [practitioners, practitionersOnline, nPractitionerOnline]);

  const handleClickOpen = () => {
    analyticsEvent({
      category: 'Booking',
      action: 'ClickPractitionerList',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (isLoading)
    return (
      <Box textAlign="center">
        <Loader size={20} color="secondary" />
      </Box>
    );

  if (!practitioners.length) {
    return <span>{t('no-practitioners-centers')}</span>;
  }

  const onPractitionerClick = (practitionerObj: any) => {
    onReset();
    updatePractitioner(practitionerObj);
    setFieldValue(field.name, practitionerObj.professional.practitionerId);
    handleClose();
  };

  return (
    <div>
      <div className="select-prat">
        {practitionerSelected && (
          <div className="selected--item">
            <p>
              <span>
                {`${
                  practitionerSelected?.speciality?.isParamedical === false
                    ? t('professional_title')
                    : ''
                }${practitionerSelected?.firstName} ${
                  practitionerSelected?.lastName
                }`}
              </span>
              <br />
              {practitionerSelected?.speciality?.name}
            </p>
          </div>
        )}
        {practitionersOnline.length > 1 && (
          <Button
            sx={{ mt: 2 }}
            color="primary"
            variant="outlined"
            onClick={handleClickOpen}
            data-cy="btn-select-prat"
          >
            {t('select-practitioner')}
          </Button>
        )}
      </div>
      <SelectDialog
        open={open}
        className="prat-select popup"
        onClose={handleClose}
        options={practitionersOnlineWithNoPrefer}
        title={t('select-practitioner')}
        onPractitionerClick={onPractitionerClick}
        {...rest}
      />
    </div>
  );
};

export default PractitionerSelect;
